import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Trash2, ArrowLeft, Instagram, Loader2, LogOut } from "lucide-react";
import { AdminLogin } from "../components/AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_CONFIG = { withCredentials: true };

export default function ReelsAdmin() {
  const location = useLocation();
  const initialUser = useRef(location.state?.user || null).current;
  const initialAuthError = useRef(location.state?.authError || "").current;
  const [user, setUser] = useState(initialUser);
  const [checking, setChecking] = useState(!initialUser);
  const [authError, setAuthError] = useState(initialAuthError);
  const [reels, setReels] = useState([]);
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);

  const handleAuthFailure = useCallback(() => {
    setUser(null);
    setAuthError("Your admin session has expired. Please sign in again.");
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reels`, AUTH_CONFIG);
      setReels(data);
    } catch (err) {
      if (err.response?.status === 401) handleAuthFailure();
    }
  }, [handleAuthFailure]);

  useEffect(() => {
    if (initialUser) {
      setChecking(false);
      return;
    }
    let active = true;
    axios.get(`${API}/auth/me`, AUTH_CONFIG)
      .then(({ data }) => {
        if (active) setUser(data);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => { active = false; };
  }, [initialUser]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const addUrls = async () => {
    const list = urls.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Paste at least one reel URL."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/reels/bulk`, { urls: list }, AUTH_CONFIG);
      toast.success(`${data.length} reel${data.length !== 1 ? "s" : ""} saved.`);
      setUrls("");
      await load();
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleAuthFailure();
      } else {
        toast.error("Couldn't save reels. Check the URLs and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this reel from the site?")) return;
    try {
      await axios.delete(`${API}/reels/${id}`, AUTH_CONFIG);
      toast.success("Removed.");
      await load();
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleAuthFailure();
      } else {
        toast.error("Couldn't remove that reel.");
      }
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, AUTH_CONFIG);
    } finally {
      setUser(null);
      setReels([]);
      setAuthError("");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background grid place-items-center" data-testid="admin-auth-loading">
        <Loader2 className="animate-spin text-accent" size={28} />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <AdminLogin
          initialError={authError}
          onLogin={(admin) => {
            setAuthError("");
            setUser(admin);
          }}
        />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="admin-back" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <p className="font-serif text-primary text-xl hidden sm:block">Reels admin</p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:inline" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-tight text-muted-foreground hover:text-accent transition-colors">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14" data-testid="admin-panel">
        <div className="mb-3 inline-flex items-center gap-2 overline text-accent"><Instagram size={13} /> Manage reels</div>
        <h1 className="font-serif text-primary text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Paste an Instagram reel link.<br />It appears on the home page instantly.
        </h1>
        <p className="mt-5 text-muted-foreground max-w-xl">
          Add one or many at a time — one URL per line. Any Instagram reel, post, or IGTV link works.
        </p>

        <div className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <label htmlFor="urls" className="text-sm font-semibold text-foreground">Reel URLs</label>
          <Textarea
            id="urls"
            data-testid="admin-urls"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={5}
            placeholder={"https://www.instagram.com/reel/C1AbCdEfGhI/\nhttps://www.instagram.com/reel/C2XyZzYyXxW/"}
            className="mt-3 font-mono text-sm"
          />
          <div className="mt-5 flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">Duplicates are ignored automatically.</p>
            <Button
              onClick={addUrls}
              disabled={busy}
              data-testid="admin-add-btn"
              className="rounded-full px-7 py-5 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
            >
              {busy ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Saving…</>) : "Add reels"}
            </Button>
          </div>
        </div>

        <div className="mt-14">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-primary text-2xl">Current reels</h2>
            <span className="text-sm text-muted-foreground" data-testid="admin-count">{reels.length} live</span>
          </div>
          {reels.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">
              No reels yet. Paste your first link above.
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reels.map((r) => (
                <li key={r.id} data-testid={`admin-reel-${r.shortcode}`} className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="bg-secondary/20" style={{ aspectRatio: "9/16" }}>
                    <iframe
                      title={r.shortcode}
                      src={`https://www.instagram.com/reel/${r.shortcode}/embed/`}
                      className="w-full h-full"
                      allow="encrypted-media"
                      loading="lazy"
                      scrolling="no"
                      frameBorder="0"
                    />
                  </div>
                  <div className="p-4 flex items-center justify-between gap-3 border-t border-border">
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground truncate hover:text-primary transition-colors">
                      {r.shortcode}
                    </a>
                    <button
                      data-testid={`admin-delete-${r.shortcode}`}
                      onClick={() => remove(r.id)}
                      className="inline-flex items-center gap-1 text-xs text-destructive hover:text-primary transition-colors"
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
